import React, { useEffect, useState } from "react";
import "./question-form.component.scoped.scss";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { RolesService } from "../../../../../services/roles.services";
import { RiskLevelsService } from "../../../../../services/riskLevels.services";
import { QuestionsService } from "../../../../../services/questions.services";
import { showPopupToastMsg, showToastMsg } from "../../../../../helpers";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Switch, { SwitchProps } from "@material-ui/core/Switch";
import { LegalsService } from "../../../../../services/legals.services";
import { StagesService } from "../../../../../services/stages.services";

const SwitchUI = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 45,
  height: 24,
  padding: 0,
  margin: "0 5px",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "2px 3px",
    transitionDuration: "300ms",
    top: 1,
    "&.Mui-checked": {
      transform: "translateX(21px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "var(--accent-color)",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      backgroundColor: "#E9E9EA",
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 18,
    height: 18,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

interface Legal {
  id: number;
  name: string;
}

interface FormData {
  question: string;
  stage: number;
  weight: number | number;
  active: boolean;
  categories?: any;
  legals: number[];
}
const initFormData: FormData = {
  question: "",
  stage: 0,
  weight: 0,
  active: false,
  legals: [],
};

const QuestionFormComponent = (props: any) => {
  const { t } = useTranslation();
  const { editable, question, onChangeQuestion } = props;
  const roleServices: RolesService = new RolesService();
  const stagesServices: StagesService = new StagesService();
  const legalsServices: LegalsService = new LegalsService();
  const riskLevelsService: RiskLevelsService = new RiskLevelsService();
  const questionsService: QuestionsService = new QuestionsService();

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [roles, setRoles] = useState<any>([]);
  const [stages, setStages] = useState<any>([]);
  const [riskLevels, setRiskLevels] = useState<any>([]);
  const [legals, setLegals] = useState<Legal[]>([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<FormData>(initFormData);

  const handleSelect = (
    riskLevelId: number,
    roleId: number,
    isChecked: boolean,
  ) => {
    setSelectedCategories((prev: any) => {
      if (isChecked) {
        // Add combination if not already selected
        if (
          !prev.some(
            (item: any) =>
              item.riskLevelId === riskLevelId && item.roleId === roleId,
          )
        ) {
          return [...prev, { riskLevelId, roleId }];
        }
        return prev;
      } else {
        // Remove the combination
        return prev.filter(
          (item: any) =>
            !(item.riskLevelId === riskLevelId && item.roleId === roleId),
        );
      }
    });
  };

  const isFormValid = Object.values(formData).every((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim() !== "";
    return value !== null && value !== undefined && value !== "" && value !== 0;
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "weight" ? Number(value) : value,
    }));
  };

  const handleLegalChange = (id: number) => {
    setFormData((prev: any) => {
      const newLegals = prev.legals.includes(id)
        ? prev.legals.filter((item: number) => item !== id) // uncheck
        : [...prev.legals, id]; // check
      return { ...prev, legals: newLegals };
    });
  };

  const submit = async () => {
    setSaving(true);
    formData.categories = selectedCategories;
    console.log(question);
    const response =
      question && question.id
        ? await questionsService.update(question.id, formData)
        : await questionsService.create(formData);
    if (response && response.message === "SUCCESS") {
      showToastMsg(
        "success",
        editable
          ? t("SETTINGS_PAGES.QUESTIONS_BUILDER.QUESTION_UPDATED_SUCCESSFULLY")
          : t("SETTINGS_PAGES.QUESTIONS_BUILDER.QUESTION_ADDED_SUCCESSFULLY"),
      );
      setFormData(initFormData);
      setSelectedCategories([]);
      onChangeQuestion?.();
    } else {
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
    setSaving(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const roles = await roleServices.list();
      const riskLevels = await riskLevelsService.list();
      const stages = await stagesServices.list();
      const legals: Legal[] = await legalsServices.list();
      setRoles(roles);
      setRiskLevels(riskLevels);
      setStages(stages);
      setLegals(legals);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (question && question.id) {
      setFormData({
        question: question.question,
        stage: question.stage.id,
        weight: question.weight,
        active: question.active,
        legals: question.legals.map((legal: any) => legal.id),
      });
      setSelectedCategories(question.categories);
    }
  }, [question]);

  return (
    <Box className="question__builder_form">
      <Box className="global__box__content p-0">
        <Box className="title">
          <Typography variant="body1" style={{ fontWeight: 400 }}>
            {t("SETTINGS_PAGES.QUESTIONS_BUILDER.CREATE_QUESTION")}
          </Typography>
        </Box>
        <Box className="content">
          <Box className="row-col-2">
            <Typography variant="subtitle2" className="pt-2">
              {t("QUESTION")}*
            </Typography>
            <Box className="w-100">
              <TextField
                className="text-area-multiline"
                fullWidth
                label={t("SETTINGS_PAGES.QUESTIONS_BUILDER.ADD_QUESTION")}
                onChange={handleChange}
                value={formData.question || ""}
                name="question"
                variant="outlined"
                multiline
                rows={4}
                inputProps={{
                  maxLength: 500,
                }}
              />
              <Typography variant="body1">
                {t(
                  "SETTINGS_PAGES.QUESTIONS_BUILDER.LIBRARIES_APIS_OTHER_COMPONENTS",
                )}
              </Typography>
            </Box>
          </Box>
          <Box className="row-col-2">
            <Typography variant="subtitle2">{t("STAGE")}*</Typography>
            <Box className="standard_text_input">
              <FormControl className="select-input w-100">
                <Select
                  labelId="stage"
                  name="stage"
                  onChange={handleChange}
                  IconComponent={ExpandMoreIcon}
                  value={formData.stage || 0}
                >
                  {/* Placeholder */}
                  <MenuItem value="0" disabled>
                    {t("SETTINGS_PAGES.QUESTIONS_BUILDER.SELECT_STAGE")}
                  </MenuItem>
                  {stages.map((value: any, index: number) => {
                    return (
                      <MenuItem key={index} value={value.id}>
                        {value.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Typography variant="body1">
                {t(
                  "SETTINGS_PAGES.QUESTIONS_BUILDER.LIBRARIES_APIS_OTHER_COMPONENTS",
                )}
              </Typography>
            </Box>
          </Box>
          <Box className="row-col-2">
            <Typography variant="subtitle2">{t("LEGAL")}*</Typography>
            <Box>
              {legals.map((legal: Legal) => (
                <Box className="checkbox-global" key={legal.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.legals.includes(legal.id)}
                        onChange={() => handleLegalChange(legal.id)}
                        name="legals"
                        size="small"
                        color="primary"
                      />
                    }
                    label={legal.name}
                  />
                </Box>
              ))}
              <Typography variant="body1">
                {t(
                  "SETTINGS_PAGES.QUESTIONS_BUILDER.BASE_OF_LEGAL_LAW_REQUIRMENTS_FOR_PROVIDER_AREA",
                )}
              </Typography>
            </Box>
          </Box>
          <Box className="row-col-2">
            <Typography variant="subtitle2">{t("WEIGHT")}*</Typography>
            <Box className="standard_text_input">
              <TextField
                style={{ width: "5rem" }}
                variant="standard"
                name="weight"
                type="number"
                value={formData.weight || 0}
                onChange={handleChange}
                inputProps={{
                  maxLength: 30,
                }}
              />
              <Typography variant="body1">
                {t(
                  "SETTINGS_PAGES.QUESTIONS_BUILDER.THIS_SERVES_TO_RANK_THE_QUESTION_WITHIN_THE_STAGE",
                )}
              </Typography>
            </Box>
          </Box>
          <Box className="row-col-2">
            <Typography variant="subtitle2">{t("ACTIVE")}</Typography>
            <Box className="standard_text_input">
              <FormControlLabel
                className="switch-global m-0"
                labelPlacement="start"
                label={""}
                control={
                  <SwitchUI
                    onChange={(e: any) => {
                      const { checked } = e.target;
                      setFormData((prev) => ({
                        ...prev,
                        active: checked,
                      }));
                    }}
                    name="active"
                    size="small"
                    color="primary"
                    checked={!!formData?.active}
                  />
                }
              />
            </Box>
          </Box>
          <Box className="mt-3" style={{ textAlign: "end" }}>
            <Button
              disabled={
                !isFormValid || saving || selectedCategories.length === 0
              }
              onClick={submit}
              className="btn-primary-theme"
              variant="contained"
              color="primary"
            >
              {editable
                ? t("SETTINGS_PAGES.QUESTIONS_BUILDER.UPDATE_QUESTION")
                : t("SETTINGS_PAGES.QUESTIONS_BUILDER.CREATE_QUESTION")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box
        className="global__box__content p-0"
        style={{ height: "fit-content" }}
      >
        <Box className="title">
          <Typography variant="body1" style={{ fontWeight: 400 }}>
            {t(
              "SETTINGS_PAGES.QUESTIONS_BUILDER.ASSIGNMENT_OF_ROLES_TO_RISK_LEVEL",
            )}
          </Typography>
        </Box>
        <Box className="matrix">
          <table>
            <thead>
              <tr>
                <th>#</th>
                {roles.map((role: any) => (
                  <th key={role.id}>
                    <Typography variant="body1">{role.name}</Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {riskLevels.map((risk: any) => (
                <tr key={risk.id}>
                  <td>
                    <Typography variant="body1">{risk.name}</Typography>
                  </td>
                  {roles.map((role: any) => {
                    const checked = selectedCategories.some(
                      (item: any) =>
                        item.riskLevelId === risk.id && item.roleId === role.id,
                    );
                    return (
                      <td key={`${risk.id}_${role.id}`}>
                        <Box className="checkbox-global">
                          <Checkbox
                            checked={checked}
                            onChange={(e: any) =>
                              handleSelect(risk.id, role.id, e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        </Box>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionFormComponent;
